import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { RoleType } from '../admin/role-type.enum';
import * as SelectedPartnerActions from '@hub-store/selected-partner/selected-partner.actions';
import * as PartnersListActions from '@hub-store/partners-list/partners-list.actions';
import { User, UserService } from '../services/user.service';
import { AuthRedirectInitiatedError } from './auth-redirect-initiated-error';
import { getPartnersList, getPartnersType } from '@hub-store/partners-list/partners-list.selectors';
import { Partner } from '../services/partner';
import { PartnersService } from '@hub-services/partners.service';
import { PartnerType } from '../admin/admin-partners/partner-type.enum';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class PartnerAccessGuard {

  constructor(
    private userService: UserService,
    private store: Store,
    private router: Router,
    private partnersService: PartnersService,
    private authService: AuthService,
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    return this.authService.waitForIdle().pipe(
      switchMap(() => this.userService.getUser().pipe(take(1))),
      switchMap((user: User | null) => {
        if (!user || !user.role) {
          return of(this.router.createUrlTree(['/access-denied']));
        }

        if (this.isAdmin(user)) {
          return of(true);
        }

        const partnerType = user.role === RoleType.OBSERVER ? PartnerType.CHANNEL : PartnerType.AIRLINE;

        return this.store.select(getPartnersList).pipe(
          concatLatestFrom(() => this.store.select(getPartnersType)),
          take(1),
          switchMap(([partnersList, cachedType]) => {
            // Only trust the cached list when it holds the type this user's role requires.
            // The shared partnersList slice is populated per-page (AdminPartnersResolve),
            // so a prior page can leave a list of the wrong type; refetch on mismatch.
            const cacheValid = !!partnersList && cachedType === partnerType;
            const partners$ = cacheValid
              ? of(partnersList)
              : this.partnersService.getPartnersV2Observable(partnerType).pipe(
                  take(1),
                  map(fetched => this.sortAlphabetically(fetched)),
                  tap(fetched => {
                    this.store.dispatch(PartnersListActions.setPartners({ partners: fetched }));
                    this.store.dispatch(PartnersListActions.setPartnerType({ partnerType }));
                  }),
                );

          return partners$.pipe(
            map(partners => {
              const hasAccess = this.userHasPartnerAccess(user, partners);
              if (!hasAccess) {
                this.store.dispatch(SelectedPartnerActions.resetPartnerState());
                return this.router.createUrlTree(['/access-denied']);
              }
              return true;
              })
            );
          })
        );
      }),
      catchError((err: unknown) => {
        if (err instanceof AuthRedirectInitiatedError) {
          return of(false);
        }
        return throwError(() => err);
      })
    );
  }

  canActivateChild(): Observable<boolean | UrlTree> {
    return this.canActivate();
  }

  private isAdmin(user: User): boolean {
    return [RoleType.ADMIN].includes(user.role);
  }

  private sortAlphabetically(partners: Partner[]): Partner[] {
    return [...partners].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  private userHasPartnerAccess(user: User, partners: Partner[]): boolean {
    const validPartnersList = user.org_access?.filter(org_name => partners.some(partner => partner.name === org_name));
    if (!validPartnersList || validPartnersList.length === 0) {
      console.log('No valid partners found for user.');
      return false;
    } else if (validPartnersList.length < (user.org_access?.length ?? 0)) {
      console.log('Some user partners are invalid and will be ignored.');
    }
    return true;
  }
}
